import { useEffect } from 'react'
import { useNetworkStatus } from '@/shared/stores/network-status.store'
import { CHUNK_LOAD_FAILED_EVENT } from '@/shared/lib/safe-navigate'
import { LogoPreLoader } from '@/shared/ui/icons'

const cx = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(' ')

interface ErrorFallbackProps {
  error: Error
  resetErrorBoundary: () => void
}

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed/i

// Short-lived guard to prevent a reload loop if the network is flapping
// or the backend is broken. Reset on successful load.
const RELOAD_GUARD_KEY = 'chunk-reload-in-progress'

export function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const { isOnline } = useNetworkStatus()
  const isChunkError = CHUNK_ERROR_PATTERN.test(error?.message ?? '')

  // Offline chunk error → hand off to the global OfflineModal.
  // The modal will auto-reload once the user comes back online
  // (see the other effect below).
  useEffect(() => {
    if (isChunkError && !isOnline) {
      window.dispatchEvent(
        new CustomEvent(CHUNK_LOAD_FAILED_EVENT, {
          detail: { reset: resetErrorBoundary },
        }),
      )
    }
  }, [isChunkError, isOnline, resetErrorBoundary])

  // Chunk error + online → smooth reload.
  //
  // Why reload and not just `resetErrorBoundary()`?
  // Once a dynamic `import()` fails, browsers cache the rejected Promise
  // in the ESM module registry. Subsequent imports of the same URL return
  // the cached failure — remounting the component re-triggers the same
  // error. A full page reload is the only reliable way to clear the
  // registry. To keep the UX invisible we hide the error card and show
  // the project's own LogoPreLoader, matching the initial app boot.
  useEffect(() => {
    if (!isChunkError || !isOnline) return
    // Prevent reload loops — if a reload has already been attempted in
    // this session for this URL, stop and show the error card instead.
    const pendingUrl = sessionStorage.getItem(RELOAD_GUARD_KEY)
    if (pendingUrl === window.location.href) {
      // Second failure after reload: give up and show the generic error.
      sessionStorage.removeItem(RELOAD_GUARD_KEY)
      return
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, window.location.href)
    // Small delay so React has a chance to render the preloader before
    // the navigation happens.
    const id = window.setTimeout(() => {
      window.location.reload()
    }, 80)
    return () => window.clearTimeout(id)
  }, [isChunkError, isOnline])

  // Chunk error → always render the preloader instead of the error card.
  // Whether we're offline (modal will appear over this) or online (reload
  // is happening), the user should never see the raw "Error" card for
  // module-load failures.
  if (isChunkError) {
    return (
      <div
        className="flex items-center justify-center w-full min-h-[60vh]"
      >
        <LogoPreLoader size='large' />
      </div>
    )
  }

  return (
    <div
      className={cx(
        'p-6 bg-[#fff0f0] border border-[#ffcccc] rounded-xl max-w-[800px] mx-auto my-10 text-center font-[Roboto,sans-serif]',
      )}
      style={{ boxShadow: '0 4px 12px rgba(255, 0, 0, 0.15)' }}
    >
      <h1 className='text-[#d93025] text-2xl mb-4'>Error</h1>
      <p className='text-[#a94442] text-base mb-5'>{error.message}</p>
      {import.meta.env.DEV && (
        <details className='text-[#666] text-left text-sm bg-[#fdfdfd] p-2.5 rounded-lg border border-[#ddd] mb-5 whitespace-pre-wrap cursor-pointer'>
          <summary>Error Details</summary>
          <pre>{error.stack}</pre>
        </details>
      )}
      <button
        className='px-5 py-2.5 bg-[#d93025] text-white border-none rounded-lg cursor-pointer transition-colors duration-300 hover:bg-[#bf251b]'
        onClick={resetErrorBoundary}
      >
        Retry
      </button>
    </div>
  )
}

export default ErrorFallback
